import styled from '@emotion/styled';
import Button from '@kyruus/button';
import Chip from '@kyruus/chip';
import { fromTheme } from '@kyruus/ui-theme';
import { Box } from '@biome-design-system/ui/Box';
import { Link } from 'react-router-dom';
import PoweredByKyruusLogo from '../shared/powered-by-logo';
import { Pagination as SearchPagination } from './pagination';

export const AvailabilityControlsContainer = styled.div`
  margin-bottom: ${fromTheme('spacing_medium')};
`;

export const StartNewSearchLink = styled(Link)`
  margin-bottom: ${fromTheme('spacing_medium')};
`;

export const StyledFacetPanel = styled('div')`
  margin-bottom: 150px;
`;

export const StyledFacetListHeader = styled('h1')`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

export const FacetFieldSet = styled.fieldset`
  &.filter-group {
    padding: ${fromTheme('spacing_medium')} 0 ${fromTheme('spacing_medium')};
  }
`;

export const StyledLegend = styled('legend')`
  float: left;
  padding: 0 ${fromTheme('spacing_medium')};
`;

export const FacetWrapper = styled('div')`
  display: grid;
  grid-template-areas: 'facetbody facetbody' 'showmore clearfilter';
  grid-auto-rows: minmax(min-content, 0);
  grid-template-columns: repeat(2, 1fr);
  row-gap: ${fromTheme('spacing_small')};
  column-gap: ${fromTheme('spacing_medium')};
  padding-top: ${fromTheme('spacing_small')};
  clear: both;
`;

export const SearchContainer = styled.div`
  ${(props) =>
    props.v9 &&
    `
      max-width: 1440px;
      margin-right: auto;
      margin-left: auto;
      padding-left: ${fromTheme('spacing_large')(props)};
      padding-right: ${fromTheme('spacing_large')(props)};
    `}
`;

export const SearchResultsWithMapContainer = styled.div`
  display: flex;
  gap: ${fromTheme('spacing_medium')};
  width: 100%;

  @media (max-width: ${fromTheme('screen_large')}) {
    flex-direction: column;
  }
`;

export const ProviderListContainer = styled.div`
  flex: 1;
  min-width: 0; /* Prevents flexbox from overflowing */
  /* Provider card fluid range: 900px - 1200px as specified in Figma */
  max-width: 1200px;
`;

export const ValueAddProgramsContainer = styled.div`
  padding-bottom: ${fromTheme('spacing_large')};
`;
export const MapSectionContainer = styled.div`
  /* Fixed width: 375px as specified in Figma */
  width: 375px;
  flex-shrink: 0;
  position: sticky; /* Keep the container sticky while scrolling */
  top: ${fromTheme('spacing_large')};
  align-self: flex-start;
  margin-bottom: ${fromTheme('spacing_xlarge')};
  body:not(.in-iframe) & {
    height: calc(100vh - 50px);
    overflow: hidden; /* Ensure no overflow from the map */
  }
  border-radius: ${fromTheme('border_radius')};

  /* Responsive behavior */
  @media (max-width: ${fromTheme('screen_large')}) {
    display: none;
  }

  /* Slightly adjust size on very large screens for better proportions */
  @media (min-width: 1600px) {
    width: 400px;
  }
`;

export const BasicFacetBody = styled.ul`
  grid-area: facetbody;

  overflow-y: auto;
  max-height: 300px;
  width: 100%;
  margin-left: -${fromTheme('spacing_xsmall')};
  padding-left: ${fromTheme('spacing_xsmall')};
`;

export const ShowMoreResultsButton = styled(Button)`
  grid-area: showmore;
  border: none;
  color: ${fromTheme('color_link')};
  margin: 0 auto 0 ${fromTheme('spacing_medium')};
  padding: 0;

  &:hover,
  &:focus {
    background: none;
    border: none;
    text-decoration: underline;
  }
`;

export const ClearFilterLink = styled(Link)`
  grid-area: clearfilter;
  color: ${fromTheme('color_link')};
  font-size: ${fromTheme('font_size')};
  margin: 0 ${fromTheme('spacing_medium')} 0 auto;

  &:hover,
  &:focus-visible {
    background: none;
    text-decoration: underline;
    outline: none;
  }
`;

export const Pagination = styled(SearchPagination)`
  grid-area: pagination;
  margin-bottom: ${fromTheme('spacing_xlarge')};

  @media print {
    visibility: hidden;
  }
`;

export const PoweredByLogo = styled(PoweredByKyruusLogo)`
  grid-area: poweredby;
`;

export const Disclaimer = styled(Box)`
  grid-area: disclaimer;
  text-align: center;
  align-self: center;

  @media only screen and (min-width: ${fromTheme('screen_medium')}) {
    text-align: left;
  }
`;

export const SuggestionsContainer = styled.div`
  grid-area: suggestions;
`;

export const FacetChip = styled(Chip)`
  && {
    background-color: ${fromTheme('color_button')};
    color: ${fromTheme('color_text_contrast')};
    font-family: ${fromTheme('font_face')};
    font-size: ${fromTheme('font_size')};
    font-weight: ${fromTheme('font_weight')};
    &:focus,
    &:focus-within {
      background-color: ${fromTheme('color_button')};
      outline-color: ${fromTheme('color_button')};
      outline-width: 2px;
      outline-style: ${fromTheme('border_style')};
      outline-offset: 2px;
    }
  }
`;

export const RemoveFilterChipButton = styled(Button)`
  && {
    background: none;
    border: none;
    padding: 0;
    width: 25px;
    height: 25px;

    &:focus,
    &:focus-visible,
    &:active,
    &:active:not(:disabled) {
      background: none;
      outline: none;
    }
  }
`;

export const ExpandedFilterDrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  body(.in-iframe) & {
    height: 100%;
  }
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
`;
